import React, { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { cn } from '@/lib/utils';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import useScrollReveal from '@/hooks/useScrollReveal';
import { useLanguage } from '@/contexts/language/LanguageContext';
import { contextEngineeringCopy } from '@/contexts/language/translations/pages/contextEngineering';

/* ── Context layers (copy in contextEngineering.ts, matched by index) ── */
const LAYERS = [
  {
    id: 'business-glossary',
    order: '01',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
      </svg>
    ),
  },
  {
    id: 'metric-logic',
    order: '02',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25a2.25 2.25 0 01-2.25-2.25v-2.25z" />
      </svg>
    ),
  },
  {
    id: 'guardrails',
    order: '03',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
  {
    id: 'sample-questions',
    order: '04',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
      </svg>
    ),
  },
  {
    id: 'connections',
    order: '05',
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    ),
  },
];

/* ── Output channel icons (copy in contextEngineering.ts, matched by index) ── */
const OUTPUT_ICONS = ['🔮', '💬', '🤝'];

const OurWorkContextEngineering = () => {
  const [activeLayer, setActiveLayer] = useState<string | null>(null);
  const [activeComparison, setActiveComparison] = useState(0);
  useScrollReveal();
  const { language } = useLanguage();
  const c = contextEngineeringCopy[language];
  const COMPARISONS = c.comparison.items;

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={c.seo.title}
        description={c.seo.description}
        canonical="/our-work/context-engineering"
      />
      <Navigation />
      <main className="index-page flex-1">

        {/* Hero */}
        <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-white">
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#E0E7FF] rounded-full filter blur-3xl opacity-30" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FFF7EC] rounded-full filter blur-3xl opacity-50" />

          <div className="container-custom relative z-10 reveal">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#2A1E47] mb-6 leading-[1.08]">
                {c.hero.title}
              </h1>
              <p className="text-lg md:text-xl text-[#2A1E47]/80 max-w-2xl mx-auto mb-3">
                {c.hero.lead}
              </p>
              <p className="text-base text-muted-foreground max-w-xl mx-auto mb-10">
                {c.hero.sub}
              </p>
              <Link
                to="/tell-me-more"
                className="inline-flex items-center justify-center px-8 py-3 rounded-lg bg-[#FF6B35] text-white font-medium hover:bg-[#FF6B35]/90 transition-colors"
              >
                {c.hero.cta}
              </Link>
            </div>
          </div>
        </section>

        {/* The problem statement */}
        <section className="py-16 md:py-24 bg-[#2A1E47]">
          <div className="container-custom reveal">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                {c.problem.titleBefore}<span className="italic text-white/40">{c.problem.titleEmphasis}</span>{c.problem.titleAfter}
              </h2>
              <p className="text-5xl md:text-6xl font-bold text-[#FF6B35] mb-8">
                {c.problem.accent}
              </p>
              <p className="text-lg text-white/60 max-w-2xl mx-auto">
                {c.problem.text}
              </p>
            </div>
          </div>
        </section>

        {/* Before / After */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-3">
              {c.comparison.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-14">
              {c.comparison.sub}
            </p>

            {/* Question tabs */}
            <div className="flex flex-wrap justify-center gap-3 mb-10">
              {COMPARISONS.map((item, i) => (
                <button
                  key={i}
                  onClick={() => setActiveComparison(i)}
                  className={cn(
                    'px-5 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border',
                    activeComparison === i
                      ? 'bg-[#2A1E47] text-white border-[#2A1E47]'
                      : 'bg-white text-[#2A1E47]/60 border-gray-200 hover:border-[#2A1E47]/30'
                  )}
                >
                  {c.comparison.quoteOpen}{item.question}{c.comparison.quoteClose}
                </button>
              ))}
            </div>

            {/* Comparison cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {/* Without context */}
              <div className="rounded-2xl border-2 border-red-100 bg-red-50/30 p-7">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </div>
                  <span className="text-sm font-bold uppercase tracking-wider text-red-400">{c.comparison.withoutLabel}</span>
                </div>
                <p className="text-[#2A1E47]/70 text-sm leading-relaxed italic">
                  {COMPARISONS[activeComparison].without}
                </p>
              </div>

              {/* With context */}
              <div className="rounded-2xl border-2 border-emerald-100 bg-emerald-50/30 p-7">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                    <svg className="w-4 h-4 text-emerald-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <span className="text-sm font-bold uppercase tracking-wider text-emerald-500">{c.comparison.withLabel}</span>
                </div>
                <p className="text-[#2A1E47] text-sm leading-relaxed">
                  {COMPARISONS[activeComparison].with}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* The 5 layers */}
        <section className="py-20 md:py-28 bg-[#FAFAFA]">
          <div className="container-custom reveal">
            <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-3">
              {c.layers.title}
            </h2>
            <p className="text-center text-muted-foreground text-lg max-w-2xl mx-auto mb-16">
              {c.layers.sub}
            </p>

            <div className="max-w-4xl mx-auto space-y-4">
              {LAYERS.map((layerBase, layerIndex) => {
                const layer = { ...layerBase, ...c.layers.items[layerIndex] };
                const isOpen = activeLayer === layer.id;
                return (
                  <div
                    key={layer.id}
                    className={cn(
                      'rounded-2xl border transition-all duration-300 overflow-hidden',
                      isOpen ? 'border-[#FF6B35]/30 shadow-lg bg-white' : 'border-gray-100 bg-white shadow-sm hover:shadow-md'
                    )}
                  >
                    <button
                      onClick={() => setActiveLayer(isOpen ? null : layer.id)}
                      className="w-full flex items-center gap-5 p-6 md:p-7 text-left"
                    >
                      <span className="text-3xl font-bold text-[#2A1E47]/10 w-10 shrink-0">{layer.order}</span>
                      <div className={cn(
                        'w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-colors',
                        isOpen ? 'bg-[#FF6B35] text-white' : 'bg-[#E0E7FF] text-[#2A1E47]/60'
                      )}>
                        {layer.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-bold text-[#2A1E47]">{layer.title}</h3>
                        <p className="text-sm text-muted-foreground mt-0.5">{layer.short}</p>
                      </div>
                      <svg
                        className={cn('w-5 h-5 text-[#2A1E47]/30 shrink-0 transition-transform duration-300', isOpen && 'rotate-180')}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {isOpen && (
                      <div className="px-6 md:px-7 pb-7 pt-0 animate-fade-in-up">
                        <div className="ml-[60px] md:ml-[76px] border-t border-gray-100 pt-5">
                          <p className="text-muted-foreground leading-relaxed mb-5">{layer.description}</p>
                          <div className="space-y-3">
                            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6B35]">{c.layers.exampleLabel}</p>
                            {layer.examples.map((ex, i) => (
                              <div key={i} className="flex gap-3">
                                <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2 bg-[#FF6B35]/40" style={{ minWidth: 6, minHeight: 6 }} />
                                <p className="text-sm text-[#2A1E47]/70 leading-relaxed">{ex}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Visual: Context flowing into AI */}
        <section className="py-20 md:py-28 bg-white overflow-hidden">
          <div className="container-custom reveal">
            <div className="max-w-5xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-center text-[#2A1E47] mb-16">
                {c.flow.title}
              </h2>

              {/* Flow diagram */}
              <div className="relative">
                <div className="relative">
                  {/* Center line — spans only the Source/Middle timeline, not the Outputs grid below */}
                  <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#2A1E47]/5 via-[#FF6B35]/30 to-[#2A1E47]/5" />

                {/* Source: Your Business */}
                <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-12 mb-16">
                  <div className="flex-1 text-right hidden md:block" />
                  <div className="w-20 h-20 rounded-2xl bg-[#2A1E47] flex items-center justify-center z-10 shadow-lg shrink-0">
                    <svg className="w-9 h-9 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 0h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#2A1E47]">{c.flow.sourceTitle}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{c.flow.sourceSub}</p>
                  </div>
                </div>

                {/* Middle: Context Engine */}
                <div className="relative flex flex-col md:flex-row items-center gap-6 md:gap-12 mb-16">
                  <div className="flex-1 text-right hidden md:block">
                    <p className="text-sm text-[#FF6B35] font-medium">{c.flow.engineSide}</p>
                  </div>
                  <div className="w-24 h-24 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#FF4500] flex items-center justify-center z-10 shadow-xl shrink-0">
                    <svg className="w-11 h-11 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-xl font-bold text-[#FF6B35]">{c.flow.engineTitle}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{c.flow.engineSub}</p>
                  </div>
                </div>
                </div>

                {/* Outputs */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {c.flow.outputs.map((output, outIndex) => (
                    <div key={output.title} className="rounded-2xl border border-gray-100 bg-[#FAFAFA] p-6 text-center hover:shadow-md transition-shadow">
                      <span className="text-3xl mb-3 block">{OUTPUT_ICONS[outIndex]}</span>
                      <h4 className="font-bold text-[#2A1E47] mb-1">{output.title}</h4>
                      <p className="text-xs text-muted-foreground">{output.desc}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Why this matters — stats */}
        <section className="py-16 md:py-20 bg-[#2A1E47]">
          <div className="container-custom reveal">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center">
              {c.stats.map((s) => (
                <div key={s.label}>
                  <p className="text-5xl md:text-6xl font-bold text-[#FF6B35] mb-2">{s.stat}</p>
                  <p className="text-white font-medium mb-1">{s.label}</p>
                  <p className="text-white/40 text-sm">{s.sub}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Process */}
        <section className="py-20 md:py-28 bg-white">
          <div className="container-custom reveal">
            <div className="max-w-4xl mx-auto">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#FF6B35] mb-4">{c.process.eyebrow}</p>
              <h2 className="text-3xl md:text-4xl font-bold text-[#2A1E47] mb-12">
                {c.process.title}
              </h2>

              <div className="space-y-0">
                {c.process.phases.map((phase, i) => (
                  <div key={phase.title} className="relative pl-12 md:pl-16 pb-12 last:pb-0">
                    {i < 3 && (
                      <div className="absolute left-[19px] md:left-[23px] top-10 bottom-0 w-px bg-[#2A1E47]/10" />
                    )}
                    <div
                      className="absolute left-0 top-0 w-10 h-10 md:w-12 md:h-12 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md"
                      style={{ backgroundColor: i === 3 ? '#FF6B35' : '#2A1E47' }}
                    >
                      {i + 1}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#2A1E47]">{phase.title}</h3>
                        <span className="text-xs font-medium text-[#FF6B35] bg-[#FFF7EC] px-2 py-0.5 rounded-full">{phase.phase}</span>
                      </div>
                      <p className="text-muted-foreground leading-relaxed">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA banner */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF7F27] to-[#FF4500] rounded-3xl" />
          <div className="container-custom relative z-10">
            <div className="max-w-3xl mx-auto text-center pt-16 pb-8 px-4 reveal">
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4 text-white leading-tight">
                {c.cta.title}
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-10 max-w-xl mx-auto">
                {c.cta.text}
              </p>
              <div className="flex justify-center">
                <Link
                  to="/tell-me-more"
                  className="inline-flex items-center justify-center px-8 py-4 rounded-lg bg-[#2a1e48] hover:bg-[#2a1e48]/90 text-white text-base font-medium transition-all duration-300 shadow-lg border border-white/10"
                >
                  {c.cta.button}
                </Link>
              </div>
            </div>
          </div>
        </section>


      </main>
      <Footer />
    </div>
  );
};

export default OurWorkContextEngineering;
